/**
 * CR082: Source Type Summary Panel (Bucket-based)
 * Shows all captured source types grouped by bucket, with usage info
 */

import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Check, AlertCircle, Info, FolderOpen } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { MerchantSource } from "@/hooks/useMerchantSources";
import { 
  SOURCE_TYPE_BUCKETS,
  BUCKET_LABELS,
  BUCKET_DESCRIPTIONS,
  bucketForSourceType,
  getSourceTypeLabel,
  getSourceTypeUsage,
  normalizeSourceType,
  type CanonicalSourceType,
  type SourceTypeBucket,
} from "@/lib/sourceTypes";

interface SourceTypeSummaryProps {
  sources: MerchantSource[];
}

interface SourceTypeData {
  type: CanonicalSourceType;
  label: string;
  usage: string;
  activeCount: number;
  totalCount: number;
  merchants: Array<{
    merchantId: string;
    merchantName: string;
    sources: MerchantSource[];
    selectedSource: MerchantSource | null;
  }>;
}

interface BucketData {
  bucket: SourceTypeBucket;
  label: string;
  description: string;
  activeCount: number;
  totalCount: number;
  sourceTypes: SourceTypeData[];
}

export function SourceTypeSummary({ sources }: SourceTypeSummaryProps) {
  const bucketData = useMemo(() => {
    // First, group sources by normalized type
    const typeMap: Record<CanonicalSourceType, SourceTypeData> = {} as any;
    const merchantTypeMap: Record<string, Record<CanonicalSourceType, MerchantSource[]>> = {};
    
    sources.forEach((source) => {
      const normalizedType = normalizeSourceType(source.source_type);
      
      // Initialize type data if needed
      if (!typeMap[normalizedType]) {
        typeMap[normalizedType] = {
          type: normalizedType,
          label: getSourceTypeLabel(normalizedType),
          usage: getSourceTypeUsage(normalizedType),
          activeCount: 0,
          totalCount: 0,
          merchants: [],
        };
      }
      
      typeMap[normalizedType].totalCount++;
      if (source.status === "active") {
        typeMap[normalizedType].activeCount++;
      }
      
      // Group by merchant
      if (!merchantTypeMap[source.merchant_id]) {
        merchantTypeMap[source.merchant_id] = {} as any;
      }
      if (!merchantTypeMap[source.merchant_id][normalizedType]) {
        merchantTypeMap[source.merchant_id][normalizedType] = [];
      }
      merchantTypeMap[source.merchant_id][normalizedType].push(source);
    });
    
    // Build merchant lists for each type
    Object.entries(merchantTypeMap).forEach(([merchantId, typeToSources]) => {
      Object.entries(typeToSources).forEach(([type, merchantSources]) => {
        const canonicalType = type as CanonicalSourceType;
        if (!typeMap[canonicalType]) return;
        
        const sortedSources = [...merchantSources].sort((a, b) => a.priority - b.priority);
        const activeSources = sortedSources.filter((s) => s.status === "active");
        const selectedSource = activeSources.length > 0 ? activeSources[0] : null;
        
        typeMap[canonicalType].merchants.push({
          merchantId,
          merchantName: merchantSources[0].merchant_name || merchantId,
          sources: sortedSources,
          selectedSource,
        });
      });
    });
    
    // Sort merchants alphabetically within each type
    Object.values(typeMap).forEach((data) => {
      data.merchants.sort((a, b) => a.merchantName.localeCompare(b.merchantName));
    });
    
    // Now group types into buckets
    const buckets: BucketData[] = SOURCE_TYPE_BUCKETS.map((bucket) => {
      const bucketTypes = Object.values(typeMap).filter(
        (data) => bucketForSourceType(data.type) === bucket
      );
      
      return {
        bucket,
        label: BUCKET_LABELS[bucket],
        description: BUCKET_DESCRIPTIONS[bucket],
        activeCount: bucketTypes.reduce((sum, t) => sum + t.activeCount, 0),
        totalCount: bucketTypes.reduce((sum, t) => sum + t.totalCount, 0),
        sourceTypes: bucketTypes.sort((a, b) => b.activeCount - a.activeCount),
      };
    });
    
    return buckets;
  }, [sources]);
  
  const totalActive = sources.filter((s) => s.status === "active").length;
  const uniqueMerchants = new Set(sources.map((s) => s.merchant_id)).size;
  const bucketsWithSources = bucketData.filter((b) => b.totalCount > 0);

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="source-type-summary" className="border rounded-lg">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center gap-3 w-full pr-4">
            <FolderOpen className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1 text-left">
              <span className="font-semibold">Source Type Summary</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {totalActive} active sources across {uniqueMerchants} merchants
              </span>
            </div>
            <Badge variant="secondary" className="text-xs">
              {bucketsWithSources.length} buckets
            </Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <p className="text-sm text-muted-foreground mb-4">
            Overview of all source types grouped by category, their usage in pipelines, and effective selection per merchant.
          </p>
          
          {/* Nested bucket accordions - only show non-empty buckets */}
          <Accordion type="multiple" className="w-full space-y-2">
            {bucketData
              .filter((bucket) => bucket.totalCount > 0)
              .map((bucket) => (
              <AccordionItem 
                key={bucket.bucket} 
                value={bucket.bucket}
                className="border rounded-md"
              >
                <AccordionTrigger className="px-3 py-2 hover:no-underline">
                  <div className="flex items-center gap-3 w-full pr-4">
                    <div className="flex-1 text-left">
                      <span className="font-medium">{bucket.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {bucket.activeCount > 0 && (
                        <Badge variant="default" className="text-xs">
                          {bucket.activeCount} active
                        </Badge>
                      )}
                      {bucket.totalCount > bucket.activeCount && (
                        <Badge variant="outline" className="text-xs">
                          {bucket.totalCount - bucket.activeCount} inactive
                        </Badge>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  {/* Bucket description */}
                  <div className="p-2 bg-muted/50 rounded text-sm mb-3">
                    <span className="font-medium">Purpose:</span>{" "}
                    {bucket.description}
                  </div>
                  
                  <div className="space-y-3">
                    {bucket.sourceTypes.map((typeData) => (
                      <SourceTypeSection key={typeData.type} typeData={typeData} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function SourceTypeSection({ typeData }: { typeData: SourceTypeData }) {
  return (
    <div className="border rounded p-3 bg-background">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-medium text-sm">{typeData.label}</span>
        <span className="text-xs text-muted-foreground font-mono">
          ({typeData.type})
        </span>
        <div className="flex-1" />
        <Badge variant="secondary" className="text-xs">
          {typeData.activeCount} active
        </Badge>
        {typeData.totalCount > typeData.activeCount && (
          <Badge variant="outline" className="text-xs">
            {typeData.totalCount - typeData.activeCount} inactive
          </Badge>
        )}
      </div>
      
      <p className="text-xs text-muted-foreground mb-2">
        <span className="font-medium">Usage:</span> {typeData.usage}
      </p>
      
      {typeData.merchants.length > 0 && (
        <div className="max-h-[200px] overflow-y-auto space-y-1 mt-2 border-t pt-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Merchants ({typeData.merchants.length})
          </div>
          {typeData.merchants.map(({ merchantId, merchantName, sources: merchantSources, selectedSource }) => (
            <div 
              key={merchantId} 
              className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/30 text-sm"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-xs truncate">{merchantName}</div>
                {selectedSource ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Check className="h-3 w-3 text-primary" />
                          <span className="truncate max-w-[200px]">{selectedSource.source_url}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-md">
                        <p className="font-medium">Selected Source (Priority {selectedSource.priority})</p>
                        <p className="text-xs break-all">{selectedSource.source_url}</p>
                        {selectedSource.source_label && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Label: {selectedSource.source_label}
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    <span>No active source</span>
                  </div>
                )}
              </div>
              {merchantSources.length > 1 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="text-xs">
                        {merchantSources.length} sources
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <div className="space-y-1">
                        {merchantSources.map((s, i) => (
                          <div key={s.id} className="text-xs">
                            <span className={s.id === selectedSource?.id ? "font-bold" : ""}>
                              #{i + 1} (P{s.priority}) {s.status}
                              {s.id === selectedSource?.id && " ← USED"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
